import is from '@sindresorhus/is';
import type { SetRequired } from 'type-fest';
import { z } from 'zod';

import { jobArchitectureGenerationInfoSchema, jobArchitectureItemSchema } from './job-architecture';
import {
    creatorSchema,
    jobArchitectureProfileReviewerSchema,
    jobProfileComponentSchema,
    jobProfileLegacySchema,
} from './job-profile';
import { versionInformationSchema } from './job-profile-versions';

export const jobLevelWithTypeSchemaTransformed = jobArchitectureItemSchema
    .extend({
        actions: z
            .object({
                canAssignReviewer: z.boolean(),
                canEdit: z.boolean(),
                canPublish: z.boolean(),
                canReadDebugInfo: z.boolean(),
            })
            .optional()
            .default({
                canAssignReviewer: false,
                canEdit: false,
                canPublish: false,
                canReadDebugInfo: false,
            }),
        creator: creatorSchema.optional(),
        isArchived: z.boolean(),
        jobProfile: jobProfileComponentSchema.optional(),
        profileGenerationInfo: jobArchitectureGenerationInfoSchema.optional(),
        reviewer: jobArchitectureProfileReviewerSchema.optional(),
        versionInfo: versionInformationSchema.optional(),
    })
    .transform((jobLevel) => ({
        ...jobLevel,
        type: 'job-level' as const,
    }));

export const jobLevelWithLegacyJobProfileSchemaTransformed = jobArchitectureItemSchema
    .extend({
        actions: z
            .object({
                canAssignReviewer: z.boolean(),
                canEdit: z.boolean(),
                canPublish: z.boolean(),
                canReadDebugInfo: z.boolean(),
            })
            .optional()
            .default({
                canAssignReviewer: false,
                canEdit: false,
                canPublish: false,
                canReadDebugInfo: false,
            }),
        creator: creatorSchema.optional(),
        isArchived: z.boolean(),
        jobProfile: jobProfileLegacySchema,
        profileGenerationInfo: jobArchitectureGenerationInfoSchema.optional(),
        reviewer: jobArchitectureProfileReviewerSchema.optional(),
    })
    .transform((jobLevel) => ({
        ...jobLevel,
        type: 'job-level' as const,
    }));

export type JobLevel = z.infer<typeof jobLevelWithTypeSchemaTransformed>;

export type JobLevelWithJobProfile = SetRequired<JobLevel, 'jobProfile'>;

export type JobLevelWithLegacyJobProfile = z.infer<typeof jobLevelWithLegacyJobProfileSchemaTransformed>;

export type JobLevelWithReviewer = SetRequired<JobLevel, 'reviewer'>;

export const isJobLevelWithJobProfile = (item: {
    readonly type: string;
    readonly jobProfile?: unknown;
}): item is JobLevelWithJobProfile => {
    return item.type === 'job-level' && is.object(item.jobProfile);
};

export const isJobLevelWithReviewer = (item: {
    readonly type: string;
    readonly reviewer?: unknown;
}): item is JobLevelWithReviewer => {
    return item.type === 'job-level' && is.object(item.reviewer);
};
